/**
 * Google Sheets models (spec section 8/9; template feedback 2026-09-25) as pure cell grids plus
 * validation rules. Every report tab opens with the "Last updated {date} @ {time}" banner and a
 * blank row, then the header on row 3, like the Agency templates. Infrastructure: Platform, Task,
 * Status, Notes and Comments with a hidden stable source key; human-owned Status and Notes survive
 * regeneration by key; removed rows retire to an archive tab (A14). Employee Compliance and the
 * Vulnerabilities sheet (the flat finding list first, packages second) follow the same model.
 */
import type { EmployeeComplianceRow, InfrastructureRow, VulnerabilityDetailRow, VulnerabilitySummaryRow } from '../providers/records.js';
import { INFRASTRUCTURE_STATUS_CHOICES } from '../providers/records.js';
import { EMPLOYEE_CHECKS, EMPLOYEE_CHECK_LABEL, type SeverityLevel, type Tri } from '../status.js';
import { formatLongDateTime, formatShortDate } from './dates.js';
import { dedupeChildren, formatChild, type ChildInput, type RenderedChild } from './placeholders.js';

export type CellValue = string | number | boolean | null;

export interface ColumnValidation {
  columnIndex: number;
  values: readonly string[];
  strict: boolean;
  inputMessage: string;
}

/** A rich-text link on one data cell (`row` indexes `rows`, not the grid). */
export interface SheetLinkCell {
  row: number;
  column: number;
  url: string;
}

export interface SheetTabModel {
  title: string;
  /** Rows written above the header: the "Last updated …" banner and a blank row (empty on the Info tab). */
  banner: CellValue[][];
  header: string[];
  rows: CellValue[][];
  hiddenColumns: number[];
  /** Rows frozen at the top: banner rows plus the header. */
  frozenRows: number;
  wrapColumns: number[];
  boldColumns: number[];
  /** Columns whose multi-line cells carry emphasised child lines (renderer applies rich text). */
  childLineColumns: number[];
  validations: ColumnValidation[];
  columnWidths: Record<number, number>;
  /** Human-owned columns must never be overwritten by a merge. */
  humanOwnedColumns: number[];
  /** Rich-text links applied to data cells (the Vulnerabilities Asset column points at the finding). */
  links: SheetLinkCell[];
}

/** Zero-based grid row of the header (the banner sits above it). */
export function headerRowIndex(t: Pick<SheetTabModel, 'banner'>): number {
  return t.banner.length;
}

export interface SheetMeta {
  accountName: string;
  snapshotAt: string | null;
  generatedAt: string;
  coverageState: string;
  coverageNotes: readonly string[];
  provenanceDigest: string;
  appVersion?: string;
  /** IANA zone the banner time is shown in (UTC when absent). */
  timeZone?: string;
  /** Extra banner cell (C1), e.g. the employee outreach note. */
  bannerNote?: string;
}

export interface SheetModel {
  title: string;
  tabs: SheetTabModel[];
  info: SheetTabModel;
}

export const INFO_TAB_TITLE = 'Info';

/** `Last updated September 1st, 2026 @ 9:32AM` in A1, the optional note in C1, then a blank row. */
export function buildBanner(meta: Pick<SheetMeta, 'generatedAt' | 'timeZone' | 'bannerNote'>): CellValue[][] {
  const first: CellValue[] = [`Last updated ${formatLongDateTime(meta.generatedAt, meta.timeZone ?? 'UTC')}`];
  if (meta.bannerNote) first.push('', meta.bannerNote);
  return [first, []];
}

/** Every linked sheet includes account, snapshot date, generated-at date and coverage notes. */
export function buildInfoTab(meta: SheetMeta): SheetTabModel {
  const rows: CellValue[][] = [
    ['Account', meta.accountName],
    ['Snapshot', meta.snapshotAt ?? 'No snapshot'],
    ['Generated at', meta.generatedAt],
    ['Coverage', meta.coverageState],
    ...meta.coverageNotes.map((n, i) => [i === 0 ? 'Coverage notes' : '', n] as CellValue[]),
    ['Provenance digest', meta.provenanceDigest],
  ];
  if (meta.appVersion) rows.push(['App version', meta.appVersion]);
  return tab(INFO_TAB_TITLE, ['Field', 'Value'], rows, { banner: [], frozenRows: 1, wrapColumns: [1], columnWidths: { 0: 160, 1: 520 } });
}

function tab(title: string, header: string[], rows: CellValue[][], extra: Partial<SheetTabModel> = {}): SheetTabModel {
  const banner = extra.banner ?? [];
  return {
    title,
    banner,
    header,
    rows,
    hiddenColumns: [],
    frozenRows: banner.length + 1,
    wrapColumns: [],
    boldColumns: [],
    childLineColumns: [],
    validations: [],
    columnWidths: {},
    humanOwnedColumns: [],
    links: [],
    ...extra,
  };
}

// ---------------------------------------------------------------------------------------------
// Infrastructure
// ---------------------------------------------------------------------------------------------

export const INFRASTRUCTURE_HEADER = ['Platform', 'Task', 'Status', 'Notes and Comments', 'Source Key'] as const;
export const INFRASTRUCTURE_COLUMNS = { platform: 0, task: 1, status: 2, notes: 3, sourceKey: 4 } as const;
export const ARCHIVE_HEADER = ['Platform', 'Task', 'Status', 'Notes and Comments', 'Source Key', 'Retired At'] as const;

/** Child lines in a Task cell are indented like the template infra task sheet. */
export const INFRASTRUCTURE_CHILD_INDENT = '        ';

export interface InfrastructureItem {
  /** Stable source key (record key). */
  sourceKey: string;
  platform: string;
  task: string;
  children: readonly (ChildInput | string)[];
}

export interface InfrastructureMerge {
  rows: InfrastructureRow[];
  archive: (InfrastructureRow & { retiredAt: string })[];
  summary: { added: number; updated: number; preservedHuman: number; retired: number; unchanged: number };
}

export function consolidateChildren(children: readonly (ChildInput | string)[]): RenderedChild[] {
  return dedupeChildren(children, 'name_with_multiplicity');
}

/**
 * Merge freshly generated items with the previous sheet rows by hidden source key. Generated
 * columns (Platform, Task, children) are managed; Status and Notes are human-owned and preserved.
 * Rows whose key disappeared retire to the archive with their human values intact.
 */
export function mergeInfrastructureRows(items: readonly InfrastructureItem[], previous: readonly InfrastructureRow[] | null, previousArchive: readonly (InfrastructureRow & { retiredAt: string })[] = [], retiredAt = ''): InfrastructureMerge {
  const prevByKey = new Map((previous ?? []).map((r) => [r.sourceKey, r] as const));
  const archiveByKey = new Map(previousArchive.map((r) => [r.sourceKey, r] as const));
  const rows: InfrastructureRow[] = [];
  const summary = { added: 0, updated: 0, preservedHuman: 0, retired: 0, unchanged: 0 };
  const seen = new Set<string>();
  for (const item of items) {
    if (seen.has(item.sourceKey)) continue;
    seen.add(item.sourceKey);
    const children = consolidateChildren(item.children).map((c) => ({ label: c.label, multiplicity: c.multiplicity }));
    const prev = prevByKey.get(item.sourceKey) ?? archiveByKey.get(item.sourceKey) ?? null;
    const status = prev?.status ?? null;
    const notes = prev?.notes ?? null;
    const humanOwned = status !== null || notes !== null;
    const row: InfrastructureRow = { sourceKey: item.sourceKey, platform: item.platform, task: item.task, children, status, notes, humanOwned, retired: false };
    if (!prev) summary.added += 1;
    else {
      const generatedChanged = prev.platform !== item.platform || prev.task !== item.task || JSON.stringify(prev.children) !== JSON.stringify(children);
      if (generatedChanged) summary.updated += 1;
      else summary.unchanged += 1;
      if (humanOwned) summary.preservedHuman += 1;
    }
    rows.push(row);
  }
  const archive: InfrastructureMerge['archive'] = previousArchive.filter((r) => !seen.has(r.sourceKey)).map((r) => ({ ...r, retired: true }));
  for (const prev of previous ?? []) {
    if (seen.has(prev.sourceKey) || archive.some((a) => a.sourceKey === prev.sourceKey)) continue;
    archive.push({ ...prev, retired: true, retiredAt });
    summary.retired += 1;
  }
  rows.sort((a, b) => a.platform.localeCompare(b.platform) || a.task.localeCompare(b.task) || (a.sourceKey < b.sourceKey ? -1 : 1));
  archive.sort((a, b) => a.platform.localeCompare(b.platform) || a.task.localeCompare(b.task) || (a.sourceKey < b.sourceKey ? -1 : 1));
  return { rows, archive, summary };
}

/** Task cell: the ask, a blank line, then one indented line per consolidated child (template infra task sheet). */
export function infrastructureTaskCell(row: Pick<InfrastructureRow, 'task' | 'children'>): string {
  if (row.children.length === 0) return row.task;
  return [row.task, '', ...row.children.map((c) => `${INFRASTRUCTURE_CHILD_INDENT}${formatChild({ label: c.label, multiplicity: c.multiplicity, identities: [] })}`)].join('\n');
}

export function infrastructureRowToCells(row: InfrastructureRow): CellValue[] {
  return [row.platform, infrastructureTaskCell(row), row.status ?? 'Pending Response', row.notes ?? '', row.sourceKey];
}

export function buildInfrastructureSheet(merge: InfrastructureMerge, meta: SheetMeta, title = 'Infrastructure'): SheetModel {
  const banner = buildBanner(meta);
  const main = tab('Infrastructure', [...INFRASTRUCTURE_HEADER], merge.rows.map(infrastructureRowToCells), {
    banner,
    hiddenColumns: [INFRASTRUCTURE_COLUMNS.sourceKey],
    wrapColumns: [INFRASTRUCTURE_COLUMNS.task, INFRASTRUCTURE_COLUMNS.notes],
    childLineColumns: [INFRASTRUCTURE_COLUMNS.task],
    validations: [{ columnIndex: INFRASTRUCTURE_COLUMNS.status, values: INFRASTRUCTURE_STATUS_CHOICES, strict: true, inputMessage: 'Pending Response or Completed' }],
    columnWidths: { 0: 160, 1: 520, 2: 160, 3: 360 },
    humanOwnedColumns: [INFRASTRUCTURE_COLUMNS.status, INFRASTRUCTURE_COLUMNS.notes],
  });
  const archive = tab('Archive', [...ARCHIVE_HEADER], merge.archive.map((r) => [...infrastructureRowToCells(r), r.retiredAt]), {
    banner,
    hiddenColumns: [INFRASTRUCTURE_COLUMNS.sourceKey],
    wrapColumns: [INFRASTRUCTURE_COLUMNS.task, INFRASTRUCTURE_COLUMNS.notes],
    columnWidths: { 0: 160, 1: 520, 2: 160, 3: 360, 5: 180 },
    humanOwnedColumns: [INFRASTRUCTURE_COLUMNS.status, INFRASTRUCTURE_COLUMNS.notes],
  });
  return { title, tabs: [main, archive], info: buildInfoTab(meta) };
}

/** Grid row holding the header: the first row (within the first five) that names both `Task` and `Source Key`; -1 when absent. */
export function findInfrastructureHeaderRow(values: readonly (readonly CellValue[])[]): number {
  for (let i = 0; i < Math.min(values.length, 5); i += 1) {
    const names = (values[i] ?? []).map((h) => (typeof h === 'string' ? h.trim().toLowerCase() : ''));
    if (names.includes('source key') && names.includes('task')) return i;
  }
  return -1;
}

/** Read an existing Infrastructure grid (banner, header row + data) back into rows, matching columns by header name. */
export function parseInfrastructureGrid(values: readonly (readonly CellValue[])[], options: { retired?: boolean } = {}): (InfrastructureRow & { retiredAt?: string })[] {
  const headerRow = findInfrastructureHeaderRow(values);
  if (headerRow < 0) return [];
  const header = values[headerRow]!;
  const idx = (name: string) => header.findIndex((h) => typeof h === 'string' && h.trim().toLowerCase() === name.toLowerCase());
  const platform = idx('Platform');
  const task = idx('Task');
  const status = idx('Status');
  const notes = idx('Notes and Comments');
  const key = idx('Source Key');
  const retiredAt = idx('Retired At');
  if (key < 0 || task < 0) return [];
  const out: (InfrastructureRow & { retiredAt?: string })[] = [];
  for (const row of values.slice(headerRow + 1)) {
    const sourceKey = String(row[key] ?? '').trim();
    if (!sourceKey) continue;
    const taskCell = String(row[task] ?? '');
    const [taskTitle, ...childLines] = taskCell.split('\n');
    const children = childLines
      .map((l) => l.replace(/^\s*•\s*/, '').trim())
      .filter(Boolean)
      .map((l) => {
        const m = /^(.*)\s\((\d+)\)$/.exec(l);
        return m ? { label: m[1]!, multiplicity: Number(m[2]) } : { label: l, multiplicity: 1 };
      });
    const statusValue = status >= 0 ? String(row[status] ?? '').trim() : '';
    const notesValue = notes >= 0 ? String(row[notes] ?? '').trim() : '';
    out.push({
      sourceKey,
      platform: platform >= 0 ? String(row[platform] ?? '') : '',
      task: taskTitle ?? '',
      children,
      status: statusValue ? statusValue : null,
      notes: notesValue ? notesValue : null,
      humanOwned: Boolean(statusValue || notesValue),
      retired: options.retired ?? false,
      ...(retiredAt >= 0 ? { retiredAt: String(row[retiredAt] ?? '') } : {}),
    });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Employee Compliance
// ---------------------------------------------------------------------------------------------

export const EMPLOYEE_SHEET_HEADER = ['Name', 'Email', 'Employment', ...EMPLOYEE_CHECKS.map((c) => EMPLOYEE_CHECK_LABEL[c])] as const;

/** Employment labels as in the template sheet (`Employee` for current staff). */
export const EMPLOYMENT_SHEET_LABEL: Record<EmployeeComplianceRow['employment'], string> = {
  current: 'Employee',
  contractor: 'Contractor',
  future_hire: 'Future hire',
  former: 'Former',
  out_of_scope: 'Out of scope',
  service_account: 'Service account',
};

/**
 * Check cells as in the template sheet: PASS / FAIL / DUE SOON / MISCONFIGURED / EXCLUDED are
 * spelled out; a check the provider gives no data for (UNKNOWN) or does not expose (UNSUPPORTED)
 * is left blank, never shown as a pass. The in-app report keeps the explicit labels.
 */
export const CHECK_SHEET_LABEL: Record<EmployeeComplianceRow['checks'][keyof EmployeeComplianceRow['checks']], string> = {
  PASS: 'PASS',
  FAIL: 'FAIL',
  DUE_SOON: 'DUE SOON',
  MISCONFIGURED: 'MISCONFIGURED',
  EXCLUDED: 'EXCLUDED',
  UNKNOWN: '',
  UNSUPPORTED: '',
};

export function employeeRowToCells(row: EmployeeComplianceRow): CellValue[] {
  return [row.name, row.email, EMPLOYMENT_SHEET_LABEL[row.employment], ...EMPLOYEE_CHECKS.map((c) => CHECK_SHEET_LABEL[row.checks[c]])];
}

export function buildEmployeeComplianceSheet(rows: readonly EmployeeComplianceRow[], meta: SheetMeta & { outreachNote?: string }, title = 'Employee Compliance'): SheetModel {
  const main = tab('Employee Compliance', [...EMPLOYEE_SHEET_HEADER], rows.map(employeeRowToCells), {
    banner: buildBanner({ ...meta, bannerNote: meta.bannerNote ?? meta.outreachNote }),
    columnWidths: { 0: 200, 1: 260, 2: 120 },
    boldColumns: [0],
  });
  const info = buildInfoTab(meta);
  if (meta.outreachNote) info.rows.push(['Outreach note', meta.outreachNote]);
  return { title, tabs: [main], info };
}

// ---------------------------------------------------------------------------------------------
// Vulnerabilities
// ---------------------------------------------------------------------------------------------

/** The template vuln sheet columns, plus the hidden keys that link the tabs. */
export const VULNERABILITY_SHEET_HEADER = ['Source', 'Asset', 'Asset id', 'Identifier', 'Package name', 'Fixed version', 'Severity', 'Due date', 'Fixable', 'Group Key', 'Finding Key'] as const;
export const VULNERABILITY_PACKAGES_HEADER = ['Package name', 'Package identifier', 'Severity', 'Asset count', 'Finding count', 'Earliest due date', 'Fixed version', 'Fixable', 'Group Key'] as const;
/** Legacy names (the flat list was the "Detail" tab, the packages the "Summary" tab). */
export const VULNERABILITY_DETAIL_HEADER = VULNERABILITY_SHEET_HEADER;
export const VULNERABILITY_SUMMARY_HEADER = VULNERABILITY_PACKAGES_HEADER;
export const VULNERABILITY_ASSET_COLUMN = 1;

/** Severity spelled out, as in the analysts' vuln sheet (the in-app chips stay CRIT / HIGH / MED). */
export const SEVERITY_SHEET_LABEL: Record<SeverityLevel, string> = { critical: 'CRITICAL', high: 'HIGH', medium: 'MEDIUM', low: 'LOW', info: 'INFO' };

export function triLabel(value: Tri): string {
  return value === 'unknown' ? 'UNKNOWN' : value ? 'Yes' : 'No';
}

/** `✅` / `❌` / `Unknown`, as in the template vuln sheet. */
export function fixableLabel(value: Tri): string {
  return value === 'unknown' ? 'Unknown' : value ? '✅' : '❌';
}

export function vulnerabilitySummaryRowToCells(row: VulnerabilitySummaryRow): CellValue[] {
  return [row.packageName, row.packageIdentifier ?? '', SEVERITY_SHEET_LABEL[row.worstSeverity], row.affectedAssetCount, row.findingCount, formatShortDate(row.earliestDueDate), row.fixedVersion ?? '', fixableLabel(row.fixable), row.groupKey];
}

export function vulnerabilityDetailRowToCells(row: VulnerabilityDetailRow): CellValue[] {
  return [row.source, row.asset, row.assetId, row.identifier, row.packageName ?? '', row.fixedVersion ?? '', SEVERITY_SHEET_LABEL[row.severity], formatShortDate(row.dueDate), fixableLabel(row.fixable), row.groupKey, row.findingKey];
}

export function buildVulnerabilitySheet(summary: readonly VulnerabilitySummaryRow[], detail: readonly VulnerabilityDetailRow[], meta: SheetMeta & { emptyReasonLabel?: string }, title = 'Vulnerabilities'): SheetModel {
  const banner = buildBanner(meta);
  // The flat finding list comes first (the template vuln sheet); the Asset cell links to the
  // provider's finding page when one is known. Group Key and Finding Key stay hidden.
  const findings = tab('Vulnerabilities', [...VULNERABILITY_SHEET_HEADER], detail.map(vulnerabilityDetailRowToCells), {
    banner,
    hiddenColumns: [VULNERABILITY_SHEET_HEADER.indexOf('Group Key'), VULNERABILITY_SHEET_HEADER.indexOf('Finding Key')],
    columnWidths: { 0: 120, 1: 240, 2: 260, 3: 150, 4: 260, 5: 150, 6: 100, 7: 110, 8: 90 },
    links: detail.flatMap((row, i) => (row.externalUrl && row.externalUrl.startsWith('https://') ? [{ row: i, column: VULNERABILITY_ASSET_COLUMN, url: row.externalUrl }] : [])),
  });
  // Packages: one row per package with the worst severity; Group Key links a package to its findings.
  const packages = tab('Packages', [...VULNERABILITY_PACKAGES_HEADER], summary.map(vulnerabilitySummaryRowToCells), {
    banner,
    hiddenColumns: [],
    columnWidths: { 0: 220, 1: 260, [VULNERABILITY_PACKAGES_HEADER.indexOf('Group Key')]: 220 },
  });
  const info = buildInfoTab(meta);
  if (summary.length === 0 && meta.emptyReasonLabel) info.rows.push(['Empty reason', meta.emptyReasonLabel]);
  return { title, tabs: [findings, packages], info };
}

/** Full grid (banner + header + rows) for values:batchUpdate. */
export function tabToGrid(t: SheetTabModel): CellValue[][] {
  return [...t.banner.map((row) => [...row]), [...t.header], ...t.rows];
}

/** Number of grid rows a tab writes (banner, header and data). */
export function tabRowCount(t: Pick<SheetTabModel, 'banner' | 'rows'>): number {
  return t.banner.length + 1 + t.rows.length;
}

/** A1 range for a whole tab (banner and header included), e.g. `'Infrastructure'!A1:E44`. */
export function tabRangeA1(t: SheetTabModel): string {
  const cols = t.header.length;
  const last = columnLetter(cols - 1);
  return `'${t.title.replace(/'/g, "''")}'!A1:${last}${tabRowCount(t)}`;
}

export function columnLetter(index: number): string {
  let n = index;
  let s = '';
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}
